import React, {
  createContext,
  HtmlHTMLAttributes,
  useCallback,
  useContext,
  useState,
} from "react";
import { IAuthToken } from "../interfaces/IAuthToken";
import { IAuthUser } from "../interfaces/IAuthUser";
import api from "../services/api";

interface IAuthState {
  user: IAuthUser;
  token: IAuthToken;
}

interface IAuthData {
  user: IAuthUser;
  token: IAuthToken;
}

interface IAuthContextData {
  user: IAuthUser;
  token: IAuthToken;
  signIn({ username, password }: ISignInCredentials): Promise<IAuthData>;
  signOut(): void;
  setData: (data: IAuthData) => void;
}

interface ISignInCredentials {
  username: string;
  password: string;
}

interface IAuthProvider extends HtmlHTMLAttributes<HTMLElement> {}

const AuthContext = createContext<IAuthContextData>({} as IAuthContextData);

const AuthProvider: React.FC<IAuthProvider> = ({ children }) => {
  const [authData, setAuthData] = useState<IAuthState>(() => {
    const token = localStorage.getItem("@ORQUIDEAS:token");
    const user = localStorage.getItem("@ORQUIDEAS:user");

    if (token && user) {
      const tokenPased: IAuthToken = JSON.parse(token);
      const userPased: IAuthUser = JSON.parse(user);

      api.defaults.headers.authorization = `Bearer ${tokenPased.accessToken}`;
      return { token: tokenPased, user: userPased };
    }

    return {} as IAuthState;
  });

  const signIn = useCallback(
    async ({ username, password }: ISignInCredentials) => {
      const response = await api.post("auth", {
        username,
        password,
      });

      const { user, token } = response.data;

      const { accessToken, refreshToken, iat, exp } = token;

      localStorage.setItem("@ORQUIDEAS:user", JSON.stringify(user));
      localStorage.setItem(
        "@ORQUIDEAS:token",
        JSON.stringify({ accessToken, refreshToken, iat, exp }),
      );

      api.defaults.headers.authorization = `Bearer ${accessToken}`;

      setAuthData({ user, token: { accessToken, refreshToken, iat, exp } });

      return { user, token: { accessToken, refreshToken, iat, exp } };
    },
    [],
  );

  const signOut = useCallback(() => {
    localStorage.removeItem("@ORQUIDEAS:token");
    localStorage.removeItem("@ORQUIDEAS:user");

    setAuthData({} as IAuthState);
  }, []);

  const setData = useCallback(({ token, user }: IAuthData) => {
    setAuthData({ token, user });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: authData.user,
        token: authData.token,
        signIn,
        signOut,
        setData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): IAuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }

  return context;
}

export { AuthProvider, useAuth };
